import { ReactElement, useMemo, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import { Button, Container } from '@mui/material'
import type { ManagedUserDTO, OrganisationDTO } from '@api/fastAPI.schemas'
import {
  getGetUsersQueryKey,
  useGetOrganisations,
  useGetUsers,
  useSyncUsers,
} from '@api/user-management/user-management'
import { useQueryClient } from '@tanstack/react-query'
import { AddButton } from '@ui/actions'
import { AlertDialog, LoadingIndicator } from '@ui/feedback'
import { BackLink } from '@ui/navigation'
import { useSnackbar } from '@ui/snackbar'
import { Box } from '@ui/structure'
import { useHasAdminPermission } from 'src/hooks'
import CreateAuth0UserDialog from './components/CreateAuth0UserDialog'
import CreateOrganisationDialog from './components/CreateOrganisationDialog'
import CreateUserDialog from './components/CreateUserDialog'
import EditOrganisationDialog from './components/EditOrganisationDialog'
import ManageMembershipsDialog from './components/ManageMembershipsDialog'
import OrganisationList from './components/OrganisationList'
import TokenSpendDashboard from './components/TokenSpendDashboard'
import UserList from './components/UserList'
import UserManagerTabs from './components/UserManagerTabs'
import { tabConfig } from './components/UserManagerTabs/constants'

type ActiveTab = keyof typeof tabConfig
type ActiveDialog = 'createUser' | 'createAuth0User' | 'createOrganisation' | null

const UserManager = (): ReactElement => {
  const { hasAdminPermission, isLoading: isLoadingPermission } = useHasAdminPermission()
  const [activeTab, setActiveTab] = useState<ActiveTab>('users')
  const [activeDialog, setActiveDialog] = useState<ActiveDialog>(null)
  const [managedUserId, setManagedUserId] = useState<string | null>(null)
  const [organisationToEdit, setOrganisationToEdit] = useState<OrganisationDTO | null>(null)
  // The Auth0 dialog acts as a sub-step when opened from the user dialog, and
  // as a standalone action when opened from the overview.
  const [auth0DialogFromUserDialog, setAuth0DialogFromUserDialog] = useState(false)
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  const { mutateAsync: syncUsers } = useSyncUsers<AxiosError, unknown>({
    mutation: {
      meta: {
        title: 'Nutzer synchronisieren',
        description: 'Gleicht Name und E-Mail aller Nutzer mit Auth0 ab',
      },
      onSuccess: async result => {
        await queryClient.invalidateQueries(getGetUsersQueryKey())
        enqueueSnackbar(
          `${result.updated} Nutzer aktualisiert, ${result.unchanged} unverändert, ` +
            `${result.without_auth0_account} ohne Auth0-Konto`,
          { variant: 'success' },
        )
      },
      onError: error => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Synchronisieren der Nutzer'), {
          variant: 'error',
        })
      },
    },
  })

  const { data: users, isLoading: isLoadingUsers } = useGetUsers<ManagedUserDTO[], AxiosError>({
    query: {
      enabled: hasAdminPermission,
      select: data => data.users,
      onError: error => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Laden der Benutzer'), {
          variant: 'error',
        })
      },
    },
  })

  const { data: organisations, isLoading: isLoadingOrganisations } = useGetOrganisations<
    OrganisationDTO[],
    AxiosError
  >({
    query: {
      enabled: hasAdminPermission,
      select: data => data.organisations,
      onError: error => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Laden der Organisationen'), {
          variant: 'error',
        })
      },
    },
  })

  // Read the managed user back out of the list rather than holding a copy, so
  // the dialog shows the new memberships as soon as the query is invalidated.
  const managedUser = useMemo(
    () => users?.find(user => user.id === managedUserId) ?? null,
    [users, managedUserId],
  )

  const openCreateAuth0UserDialog = (fromUserDialog: boolean) => {
    setAuth0DialogFromUserDialog(fromUserDialog)
    setActiveDialog('createAuth0User')
  }

  const leaveCreateAuth0UserDialog = () => {
    setActiveDialog(auth0DialogFromUserDialog ? 'createUser' : null)
  }

  if (isLoadingPermission) {
    return <LoadingIndicator />
  }

  if (!hasAdminPermission) {
    return <Navigate to="/" replace />
  }

  return (
    <Container maxWidth="lg">
      <Box paddingX={2} mb={2} display="flex" justifyContent="space-between" alignItems="center">
        <Box paddingX={2}>
          <BackLink />
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <AlertDialog
            title="Nutzer mit Auth0 synchronisieren"
            text={
              'Name und E-Mail-Adresse aller Nutzer werden aus Auth0 übernommen und ' +
              'überschreiben die Werte in der Datenbank. Felder, die in Auth0 leer sind, ' +
              'bleiben unverändert. Es werden keine neuen Nutzer angelegt.'
            }
            confirmLabel="Synchronisieren"
            onConfirm={async () => {
              // Reported by onError; swallowed so the dialog can close.
              await syncUsers().catch(() => undefined)
            }}
          >
            <Button size="small" variant="outlined" data-cy="btn-sync-users">
              Nutzer synchronisieren
            </Button>
          </AlertDialog>
          <Button
            size="small"
            variant="outlined"
            onClick={() => openCreateAuth0UserDialog(false)}
            data-cy="btn-create-auth0-user"
          >
            Auth0 Nutzer erstellen
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setActiveDialog('createOrganisation')}
            data-cy="btn-create-organisation"
          >
            Organisation erstellen
          </Button>
          <AddButton
            onClick={() => setActiveDialog('createUser')}
            data-cy="btn-create-user"
            size="small"
          >
            Nutzer erstellen
          </AddButton>
        </Box>
      </Box>
      <UserManagerTabs
        isLoading={isLoadingUsers || !users || isLoadingOrganisations || !organisations}
        userList={
          <UserList users={users || []} onManageOrganisations={user => setManagedUserId(user.id)} />
        }
        organisationList={
          <OrganisationList
            organisations={organisations || []}
            onEdit={organisation => setOrganisationToEdit(organisation)}
          />
        }
        tokenSpendDashboard={<TokenSpendDashboard />}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />
      {activeDialog === 'createUser' && (
        <CreateUserDialog
          open
          onClose={() => setActiveDialog(null)}
          onCreateAuth0User={() => openCreateAuth0UserDialog(true)}
          organisations={organisations || []}
        />
      )}
      {activeDialog === 'createAuth0User' && (
        <CreateAuth0UserDialog
          open
          onClose={leaveCreateAuth0UserDialog}
          onCreated={leaveCreateAuth0UserDialog}
        />
      )}
      {activeDialog === 'createOrganisation' && (
        <CreateOrganisationDialog open onClose={() => setActiveDialog(null)} />
      )}
      {managedUser && (
        <ManageMembershipsDialog
          user={managedUser}
          organisations={organisations || []}
          onClose={() => setManagedUserId(null)}
        />
      )}
      {organisationToEdit && (
        <EditOrganisationDialog
          organisation={organisationToEdit}
          onClose={() => setOrganisationToEdit(null)}
        />
      )}
    </Container>
  )
}

export default UserManager
